/**
 * useCompletions — SPD v5.87 · Predictive Completions (2026-02-14).
 *
 * ─────────────────────────────────────────────────────────────────
 * Contract
 * ─────────────────────────────────────────────────────────────────
 * Given a partial string + a consent config, this hook returns a
 * stable object describing the top-N completions to render and
 * whether the current partial looks like a refinement of a recent
 * query.
 *
 *   const c = useCompletions(partial, { consent: { username: true } });
 *   // c = {
 *   //   completions: [{text, score, matchType}, …],
 *   //   isRefinement: boolean,
 *   //   anchoredTo: string | null,
 *   //   partial: string,
 *   //   tookMs: number,
 *   //   source: 'idle' | 'trie' | 'trie+enriched' | 'error'
 *   // }
 *
 * ─────────────────────────────────────────────────────────────────
 * Latency guarantee
 * ─────────────────────────────────────────────────────────────────
 * The FIRST paint is always synchronous client-side (SEED + cached
 * KA + session queries). Backend enrichment via
 * `POST /api/search/completions` runs in the background with a 40 ms
 * debounce and upgrades the trie for the next keystroke. If the
 * backend is slow or the user is offline, we NEVER block first paint.
 *
 * ─────────────────────────────────────────────────────────────────
 * Consent gating
 * ─────────────────────────────────────────────────────────────────
 * The refinement-memory boost is applied only when
 * `consent.username === true`. When consent is off, the trie still
 * runs but the recent-queries bias is skipped.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { API, getToken } from '../utils/authClient';
import {
  SEED_CORPUS,
  rankCompletions,
  detectRefinement,
  getCachedKaConcepts,
  setCachedKaConcepts,
  getRefinementRecents,
} from '../lib/completionsCorpus';
import { filterStaleCompletions } from './staleCompletionsFilter';

const DEBOUNCE_MS = 40;      // KA enrichment (first-mount only)
const LIVE_DEBOUNCE_MS = 150; // Live Haiku (per-keystroke)
const LIVE_MIN_CHARS = 2;     // Don't burn Haiku on a single letter
const MAX_K = 3;              // matches wireframe spec

const EMPTY_STATE = Object.freeze({
  completions: [],
  isRefinement: false,
  anchoredTo: null,
  partial: '',
  tookMs: 0,
  source: 'idle',
});

/**
 * Merge recent-session queries + cached KA concepts + the seed
 * corpus into one deduped candidate list.
 *
 * IJ-239 (2026-08-01) · Recents are added FIRST so rankCompletions
 * (which uses a stable sort) keeps them ahead of seed items on any
 * score tie. Combined with the EXACT_RECENT_BOOST in the ranker,
 * this ensures a byte-verbatim recent query surfaces above every
 * seed suggestion when the user types a prefix of it.
 */
function buildCorpus() {
  const kaConcepts = getCachedKaConcepts();      // array of strings
  const recents = getRefinementRecents().map((r) => r.q);
  const seen = new Set();
  const out = [];
  const push = (v) => {
    const s = String(v || '').trim();
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };
  recents.forEach(push);        // IJ-239 · recents first · tie-break favours them
  kaConcepts.forEach(push);
  SEED_CORPUS.forEach(push);
  return out;
}

export function useCompletions(partial, opts = {}) {
  const consent = opts.consent || {};
  const applyRefinement = consent.username === true;
  const [state, setState] = useState(EMPTY_STATE);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const enrichedOnceRef = useRef(false);
  // SPD v5.88.1 (2026-02-15) · live Haiku completions per keystroke.
  // The trie-based first paint stays synchronous (sub-5 ms). This
  // ref holds the debounce + abort controller for the LIVE endpoint
  // so we can cancel any in-flight LLM call the moment the user
  // types another character.
  const liveDebounceRef = useRef(null);
  const liveAbortRef = useRef(null);

  // ─── SYNCHRONOUS FIRST PAINT ───────────────────────────────────
  // Runs every render — trie ranking is <5 ms so it's safe to
  // compute inline. We freeze the result in `useMemo` so the hook
  // only rebuilds when the partial or consent flag changes.
  const synchronous = useMemo(() => {
    const t0 = performance.now();
    const p = String(partial || '').trim();
    if (p.length < 1) return EMPTY_STATE;
    const corpus = buildCorpus();
    const ranked = rankCompletions(p, corpus, {
      k: MAX_K,
      applyRefinement,
    });
    const refinement = applyRefinement
      ? detectRefinement(p)
      : { isRefinement: false, anchoredTo: null };
    return {
      completions: ranked,
      isRefinement: refinement.isRefinement,
      anchoredTo: refinement.anchoredTo,
      partial: p,
      tookMs: Math.round((performance.now() - t0) * 10) / 10,
      source: 'trie',
    };
  }, [partial, applyRefinement]);

  // SPD v5.87.2 · IJ-075 (2026-07-24) — stale-while-revalidate.
  // IJ-238 (2026-08-01) — the retained prev.completions are now
  // pruned by filterStaleCompletions so any item that no longer
  // matches the current partial as a Tier-1 prefix or Tier-2
  // last-word prefix is dropped. This fixes the operator-reported
  // defect where Tier-3 substring hits from an earlier keystroke
  // (for example 'world war 2 pacific theatre' surviving the token
  // 'the' because 'theatre' contains 'the') persisted into a
  // totally unrelated query.
  //
  // The old behaviour was `setState(synchronous)` on every partial
  // change. That REPLACED the previously-shown completions, which
  // meant informational queries whose partial does not match any
  // seed-corpus entry (the trie returns []) flashed empty on every
  // keystroke while the 150 ms LIVE_DEBOUNCE + Haiku round-trip was
  // in flight. Operator's screenshot on 2026-07-24 showed the exact
  // failure: `what is the` produced no dropdown because the trie
  // returned [] and the live Haiku call was still pending or had
  // been aborted by the next keystroke.
  //
  // New behaviour mirrors the SPD v5.83 useTypeahead pattern:
  //   • Empty partial → always reset to EMPTY_STATE.
  //   • Non-empty partial + trie returned [] + previous state had
  //     completions → KEEP the SURVIVING previous completions after
  //     IJ-238 filtering (only refresh the partial + refinement
  //     flags) so the dropdown does not flash empty. The live-Haiku
  //     effect below will replace them within ~850 ms.
  //   • Non-empty partial + trie returned matches → use the fresh
  //     sync value as before.
  useEffect(() => {
    setState((prev) => {
      if (!synchronous.partial) return EMPTY_STATE;
      if (synchronous.completions.length === 0 && prev.completions.length > 0) {
        const survivors = filterStaleCompletions(prev.completions, synchronous.partial);
        if (survivors.length === 0) return synchronous;
        return {
          ...prev,
          completions: survivors,
          partial: synchronous.partial,
          isRefinement: synchronous.isRefinement,
          anchoredTo: synchronous.anchoredTo,
        };
      }
      return synchronous;
    });
  }, [synchronous]);

  // ─── BACKGROUND ENRICHMENT (best-effort, non-blocking) ─────────
  // Fires once per session on the first non-empty partial. Pulls
  // KA concepts + optional seed additions from the backend and
  // stores them in sessionStorage. Subsequent keystrokes pick up
  // the enriched corpus automatically via buildCorpus().
  useEffect(() => {
    const p = String(partial || '').trim();
    if (p.length < 1) return;
    if (enrichedOnceRef.current) return;
    enrichedOnceRef.current = true;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const token = getToken();
      if (!token) return;
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const res = await fetch(`${API}/search/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ partial: p, consent }),
          signal: ac.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.ka_concepts)) {
          setCachedKaConcepts(data.ka_concepts);
        }
        // Retrigger a re-rank now that the corpus grew.
        const t0 = performance.now();
        const corpus = buildCorpus();
        const ranked = rankCompletions(p, corpus, {
          k: MAX_K,
          applyRefinement,
        });
        setState((prev) => ({
          ...prev,
          completions: ranked,
          tookMs: Math.round((performance.now() - t0) * 10) / 10,
          source: 'trie+enriched',
        }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Silent — client-side trie is already rendered.
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [Boolean(partial)]);

  // ─── SPD v5.88.1 · LIVE HAIKU COMPLETIONS (per-keystroke) ─────
  // Fires POST /api/search/completions/live with a 150 ms debounce.
  // The endpoint is cache-first (LRU 500 entries, 10-min TTL) so
  // repeat partials hit sub-5 ms server-side.
  //
  // Results merge with the client-side trie (trie first, live after,
  // deduped) so the user sees SOMETHING immediately (from the trie)
  // and it's replaced/augmented by Haiku's suggestions when they land.
  //
  // Cancellation contract: every keystroke aborts the previous
  // in-flight fetch. Zero risk of "old completions from 2 keystrokes
  // ago flashing on screen after new ones already rendered".
  useEffect(() => {
    const p = String(partial || '').trim();
    if (p.length < LIVE_MIN_CHARS) return;

    if (liveDebounceRef.current) clearTimeout(liveDebounceRef.current);
    if (liveAbortRef.current) liveAbortRef.current.abort();

    liveDebounceRef.current = setTimeout(async () => {
      const token = getToken();
      if (!token) return;
      const ac = new AbortController();
      liveAbortRef.current = ac;
      try {
        const recents = getRefinementRecents().map((r) => r.q).slice(0, 3);
        const res = await fetch(`${API}/search/completions/live`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            partial: p,
            recent_queries: recents,
            k: MAX_K,
          }),
          signal: ac.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const liveList = Array.isArray(data.completions) ? data.completions : [];
        if (liveList.length === 0) return;

        // Merge with the client-side trie output. Trie first (already
        // rendered), then live entries that aren't duplicates.
        setState((prev) => {
          const seen = new Set(prev.completions.map((c) => (c.text || '').toLowerCase()));
          const merged = [...prev.completions];
          liveList.forEach((txt) => {
            const key = String(txt || '').toLowerCase();
            if (!key || seen.has(key)) return;
            seen.add(key);
            merged.push({
              text: txt,
              score: 0.85,  // slightly under trie top-tier (1.0) so seed wins ties
              matchType: 'haiku',
            });
          });
          return {
            ...prev,
            completions: merged.slice(0, MAX_K + 2),  // allow up to 5 shown when live augments
            source: prev.completions.length > 0 ? 'trie+haiku' : 'haiku',
          };
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Silent — client-side trie is already rendered.
      }
    }, LIVE_DEBOUNCE_MS);

    return () => {
      if (liveDebounceRef.current) clearTimeout(liveDebounceRef.current);
      if (liveAbortRef.current) liveAbortRef.current.abort();
    };
  }, [partial]);

  return state;
}

export default useCompletions;
